import { closesBusiness } from "helpers/blackouts"
import { DAY_ALERT_SLOT } from "./constants"

/**
 * Turns a day from the blackout feed into the props its label chip needs, on any surface.
 *
 * A day gets exactly one chip, whatever it carries:
 *
 * - a business closure — red, its own label, no count;
 * - one blackout — its own label ("Crew OFF"), counted;
 * - several blackouts — "Partial Blackout", counted;
 * - conflicts only — "Resource Conflict (N)" on the light surface, since a double-booked
 *   resource closes nothing (docs/blackouts-api.md D11);
 * - both a blackout and a conflict — "Multiple availability issues", naming neither. Counting
 *   them together would be a lie: they are different kinds of problem, and the day panel is
 *   where the guide reads the breakdown.
 *
 * `label` is server copy and is rendered as-is; `labelKey` is set only when the label is
 * composed on the client and must go through `t()` — use `resolveChipLabel` rather than
 * picking between them at each call site.
 */
export const getDayChipProps = (day) => {
  const blackouts = day?.blackouts || []
  const conflicts = day?.conflicts || []
  if (!blackouts.length && !conflicts.length) return null

  if (blackouts.length && conflicts.length) {
    return {
      labelKey: "calendar.blackout.multiple_issues",
      labelDefault: "Multiple availability issues",
      variant: "partial",
      count: blackouts.length + conflicts.length,
      showCount: false
    }
  }

  if (!blackouts.length) {
    // The count is inside the copy here, so it must not also be appended.
    return {
      labelKey: "calendar.blackout.resources_conflict",
      labelDefault: "Resource Conflict ({{count}})",
      variant: "conflict",
      count: conflicts.length,
      showCount: false
    }
  }

  if (day.status === "full") {
    const closingBlackout = blackouts.find(closesBusiness) || blackouts[0]
    // A full closure needs no count — it is the whole day either way.
    return { label: closingBlackout.label, variant: "full", count: blackouts.length, showCount: false }
  }

  if (blackouts.length === 1) {
    return { label: blackouts[0].label, variant: "partial", count: 1, showCount: true }
  }

  return {
    labelKey: "calendar.blackout.partial",
    labelDefault: "Partial Blackout",
    variant: "partial",
    count: blackouts.length,
    showCount: true
  }
}

/** A closure of the business, or of a listing, applies to every resource on the day. */
const DAY_WIDE_SCOPES = ["business", "listing"]

/** The day's closures that belong to nobody in particular — they shade the whole grid. */
export const getDayWideBlackouts = (day) =>
  (day?.blackouts || []).filter((blackout) => DAY_WIDE_SCOPES.includes(blackout.scope))

/**
 * "John Doe (Admin)" and "Boat for multiday - Asset #0002" both name the same thing as a row does.
 *
 * The feed decorates a resource's label with what distinguishes it inside its own list — the crew
 * member's role, the asset's number — while a grid row is labelled with the bare name. Comparing
 * them means dropping the decoration.
 */
const toResourceName = (label) =>
  String(label || "")
    .split(" - ")[0]
    .replace(/\s*\([^)]*\)\s*$/, "")
    .trim()
    .toLowerCase()

/**
 * Either identifier is enough. The id is checked first and the name is a fallback rather than a
 * tie-breaker, because the two sides are not guaranteed to be the same id-space — the blackout's
 * crew ids have already proved to be asset-level where the booking filters are equipment-level.
 */
const matchesResourceRow = (entry, { id, name }) => {
  if (id != null && entry?.id != null && String(entry.id) === String(id)) return true
  const entryName = toResourceName(entry?.label)
  return Boolean(entryName) && entryName === toResourceName(name)
}

/**
 * The day's closures belonging to one crew member or one piece of equipment.
 *
 * The day grid gives each resource a row of its own, so its closure belongs on that row rather
 * than washing the whole grid — one crew member being off says nothing about the others.
 *
 * Who a closure applies to is its `resources` list, **not** its own `id`/`label`: those describe
 * the closure itself ("Crew OFF", and the blackout record's id), so matching on them finds nothing.
 * A feed that sends no `resources` — the shape this shipped against — falls back to the closure's
 * own id and label, which is right when the record targets a single resource.
 *
 * @param {Object} day the day's entry from the blackout feed
 * @param {{scope: string, id?: string|number, name?: string}} resource the row to match
 */
export const getResourceBlackouts = (day, { scope, id, name } = {}) =>
  (day?.blackouts || []).filter((blackout) => {
    if (blackout.scope !== scope) return false
    const targets = Array.isArray(blackout.resources) ? blackout.resources : []
    if (targets.length) return targets.some((target) => matchesResourceRow(target, { id, name }))
    return matchesResourceRow({ id: blackout.id, label: blackout.label }, { id, name })
  })

/** Server copy wins; client copy goes through `t()`, with the count available to interpolate. */
export const resolveChipLabel = (chip, t) =>
  chip.label || t(chip.labelKey, { defaultValue: chip.labelDefault, count: chip.count })

/**
 * Whether a day's bookings should be outlined as caught by its blackout.
 *
 * A full closure catches every booking on the day: the column is already shaded and the banner
 * carries the count, so outlining each one only adds noise — which is why the design leaves
 * them plain. On a partial closure the outline is the only thing separating the bookings that
 * are caught from the ones that survive, so there it earns its place.
 */
export const marksBookingConflicts = (day) => Boolean(day) && day.status !== "full"

/** True when the day carries something the guide has to act on. */
export const hasBlackoutAction = (day) => Boolean(day?.bookings_requiring_action)

/**
 * Which alert rows every cell in a week must reserve, in render order.
 *
 * The chip and the action alert sit above the multi-day bars, so a trip running 27→29 reads as
 * one of the bookings the alert is counting rather than as something hanging over it. But a bar
 * is drawn once, in the cell its run starts in, and stretched across the columns it covers — so
 * it hangs at whatever offset that one cell gives it. If only the affected day made room for its
 * alert, every bar reaching that day from the left would cut straight through the alert.
 *
 * So the whole week reserves the union: a day with nothing to say still leaves the rows empty,
 * and the strip stays level across all seven columns.
 *
 * @param {Object[]} days the week's days from the blackout feed, in order, null where clear
 * @returns {string[]} `DAY_ALERT_SLOT` values, empty when no day in the week carries either row
 */
export const buildDayAlertSlots = (days = []) => {
  const slots = []
  if (days.some((day) => Boolean(getDayChipProps(day)))) slots.push(DAY_ALERT_SLOT.CHIP)
  if (days.some((day) => hasBlackoutAction(day))) slots.push(DAY_ALERT_SLOT.ACTION)
  return slots
}

/**
 * A conflict carries no id, so it is identified by its content. The feed names the resource in
 * `name` while the contract calls it `label`/`detail`; keying off only one of them collapses
 * every conflict on a day to the same string — three crew conflicts became one key.
 */
export const conflictKey = (conflict, index) =>
  `${conflict?.type || "conflict"}:${conflict?.name || conflict?.label || conflict?.detail || index}`

/**
 * Two days belong to the same banner run when exactly the same blackouts cover both — and carry
 * the same conflicts, since a conflict alone is enough to earn a day a chip.
 */
const runKey = (day) => {
  const blackoutIds = (day?.blackouts || []).map((blackout) => blackout.id)
  const conflictKeys = (day?.conflicts || []).map(conflictKey)
  return [...blackoutIds, ...conflictKeys].sort().join("|")
}

/**
 * Collapses a week into banner segments so a blackout spanning several days renders as one
 * bar across those columns, as the week design shows, instead of repeating per column.
 *
 * @param {string[]} dayKeys the week's days, in order, as "YYYY-MM-DD"
 * @param {(dateKey: string) => Object|null} getDay
 * @returns {{dateKey: string, span: number, day: Object|null}[]} one entry per segment
 */
export const buildBlackoutRuns = (dayKeys, getDay) =>
  dayKeys.reduce((runs, dateKey) => {
    const day = getDay(dateKey)
    const key = runKey(day)
    const previous = runs[runs.length - 1]

    // Clear days never merge — each keeps its own empty cell so the columns stay aligned.
    if (key && previous && runKey(previous.day) === key) {
      previous.span += 1
      return runs
    }

    runs.push({ dateKey, span: 1, day: key ? day : null })
    return runs
  }, [])
